import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Chip, Tooltip, chipClasses, lighten } from '@mui/material'
import { createStyles, makeStyles } from '@mui/styles'
import AccessTime from '@mui/icons-material/AccessTime'
import classnames from 'classnames'
import moment from 'moment'
import { useOrderAccountsContext } from 'app/workorder/contexts/use-order-account-context'
import { IBranchTrips } from '@cbgms/api/modules/wholesaler'
import { apiFormat } from '@cbgms/base/utils/datetime'
import { TimerVariant } from '@cbgms/api'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles(theme =>
  createStyles({
    chipContainer: {
      padding: theme.spacing(1),
      display: 'flex',
      alignItems: 'center'
    },
    chip: {
      fontSize: '14px',
      fontWeight: 400,
      [`& .${chipClasses.label}`]: {
        fontSize: '14px'
      }
    },
    danger: {
      fontWeight: 600,
      backgroundColor: lighten(theme.palette.error.light, 0.8),
      color: theme.palette.error.main,
      [`& .${chipClasses.icon}`]: {
        color: theme.palette.error.main
      }
    },
    warning: {
      fontWeight: 600,
      backgroundColor: lighten(theme.palette.warning.light, 0.8),
      color: theme.palette.warning.main,
      [`& .${chipClasses.icon}`]: {
        color: theme.palette.warning.main
      }
    },
    default: {
      backgroundColor: lighten(theme.palette.primaryThemeColor.dark || theme.palette.primaryThemeColor.main, 0.08),
      color: theme.palette.common.white,
      [`& .${chipClasses.icon}`]: {
        color: theme.palette.common.white
      }
    }
  })
)

interface TimerProps {
  timer: number
  onTimerComplete?: () => void
  onTimerUpdate?: (remainingSeconds: number) => void
}

const DeliveryCountdown: React.FC = () => {
  const orderAccountContext = useOrderAccountsContext()

  const trips = useMemo(
    () => orderAccountContext?.selectedOrderAccount?.BranchDetails?.Trips || [],
    [orderAccountContext?.selectedOrderAccount?.UUID]
  )

  const getCurrentDayName = useCallback(() => moment().locale('en').format('dddd'), [])

  const applicableTrips = useMemo((): IBranchTrips[] => {
    if (!trips || !Array.isArray(trips)) {
      return []
    }

    const currentDay = getCurrentDayName()
    const filteredTrips = trips.filter(
      (trip: IBranchTrips) => trip.ApplicableDays && Array.isArray(trip.ApplicableDays) && trip.ApplicableDays.includes(currentDay)
    )

    return filteredTrips
  }, [trips, getCurrentDayName])

  const getNextValidTimer = useCallback((): string | null => {
    const currentTime = moment()

    if (applicableTrips.length === 0) {
      return null
    }

    const validTimers: string[] = []

    applicableTrips.forEach((trip: IBranchTrips) => {
      if (!trip.CutOffTime || typeof trip.CutOffTime !== 'string') return

      const parsedTime = moment(trip.CutOffTime, apiFormat.timeWithSecs, true)
      if (!parsedTime.isValid()) return

      const hours = parsedTime.hours()
      const minutes = parsedTime.minutes()
      const seconds = parsedTime.seconds()

      const cutoffTime = moment().startOf('day').add({ hours, minutes, seconds })
      const timerStartTime = cutoffTime.clone().subtract(30, 'minutes')

      const isValid = currentTime.isAfter(timerStartTime) && currentTime.isBefore(cutoffTime)

      if (isValid) {
        validTimers.push(cutoffTime.format())
      }
    })

    const sortedTimers = validTimers.sort((a, b) => moment(a).diff(moment(b)))
    const nextTimer = sortedTimers.length > 0 ? sortedTimers[0] : null

    return nextTimer
  }, [applicableTrips])

  const [activeTimer, setActiveTimer] = useState<string | null>(() => getNextValidTimer())

  // Reset timer when order account changes
  useEffect(() => {
    const newTimer = getNextValidTimer()
    setActiveTimer(newTimer)
  }, [orderAccountContext?.selectedOrderAccount?.UUID, getNextValidTimer])

  const handleTripsChange = useCallback(() => {
    if (trips.length === 0) {
      setActiveTimer(null)
      return
    }

    if (trips.length > 0 && !activeTimer) {
      const newTimer = getNextValidTimer()
      if (newTimer) {
        setActiveTimer(newTimer)
      }
    }
  }, [trips.length, orderAccountContext?.selectedOrderAccount, activeTimer, getNextValidTimer])

  useEffect(() => {
    handleTripsChange()
  }, [handleTripsChange])

  const calculateRemainingSeconds = useCallback((dateTimeString: string): number => {
    const targetTime = moment(dateTimeString)
    const currentTime = moment()
    const diffInSeconds = targetTime.diff(currentTime, 'seconds')
    return Math.max(0, diffInSeconds)
  }, [])

  const handleTimerComplete = useCallback(() => {
    const nextTimer = getNextValidTimer()
    if (nextTimer) {
      setActiveTimer(nextTimer)
    } else {
      setActiveTimer(null)
    }
  }, [getNextValidTimer])

  const checkForNewTimers = useCallback(() => {
    if (trips.length === 0) {
      if (activeTimer) {
        setActiveTimer(null)
      }
      return
    }

    if (!activeTimer) {
      const nextTimer = getNextValidTimer()
      if (nextTimer) {
        setActiveTimer(nextTimer)
      }
    } else {
      const currentRemaining = calculateRemainingSeconds(activeTimer)
      if (currentRemaining <= 0) {
        handleTimerComplete()
      }
    }
  }, [activeTimer, getNextValidTimer, calculateRemainingSeconds, handleTimerComplete, trips.length])

  const getOptimalInterval = useCallback(() => {
    if (!activeTimer) {
      const nextTimer = getNextValidTimer()
      if (nextTimer) {
        const remainingSeconds = calculateRemainingSeconds(nextTimer)
        if (remainingSeconds <= 120) {
          return 10000
        }
      }
      return 30000
    }
    return 60000
  }, [activeTimer, getNextValidTimer, calculateRemainingSeconds])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    const scheduleNextCheck = () => {
      const interval = getOptimalInterval()
      timeoutId = setTimeout(() => {
        checkForNewTimers()
        scheduleNextCheck()
      }, interval)
    }

    scheduleNextCheck()
    return () => clearTimeout(timeoutId)
  }, [checkForNewTimers, getOptimalInterval])

  const remainingSeconds = useMemo(
    () => (activeTimer ? calculateRemainingSeconds(activeTimer) : 0),
    [activeTimer, calculateRemainingSeconds]
  )

  if (!activeTimer) {
    return null
  }

  if (remainingSeconds <= 0) {
    return null
  }

  return <Timer timer={remainingSeconds} onTimerComplete={handleTimerComplete} />
}

const Timer: React.FC<TimerProps> = ({ timer, onTimerComplete, onTimerUpdate }) => {
  const classes = useStyles()
  const [remainingSeconds, setRemainingSeconds] = useState(timer)
  const { t } = useTranslation()

  useEffect(() => {
    setRemainingSeconds(timer)
  }, [timer])

  const updateTimer = useCallback(() => {
    setRemainingSeconds(prev => {
      const newTime = prev - 1
      onTimerUpdate?.(newTime)
      return newTime
    })
  }, [onTimerUpdate])

  useEffect(() => {
    if (remainingSeconds <= 0) {
      onTimerComplete?.()
      return
    }

    const intervalId = setInterval(updateTimer, 1000)

    return () => clearInterval(intervalId)
  }, [remainingSeconds, onTimerComplete, updateTimer])

  const formatTime = useCallback((seconds: number): string => {
    if (seconds <= 0) return '00:00:00'

    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }, [])

  const getCurrentVariant = useCallback((seconds: number): TimerVariant => {
    const minutes = seconds / 60
    if (minutes < 5) {
      return TimerVariant.DANGER
    } else if (minutes < 10) {
      return TimerVariant.WARNING
    } else {
      return TimerVariant.DEFAULT
    }
  }, [])

  const currentVariant = useMemo(() => getCurrentVariant(remainingSeconds), [remainingSeconds, getCurrentVariant])

  const getVariantClass = useCallback(() => {
    switch (currentVariant) {
      case TimerVariant.DANGER:
        return classes.danger
      case TimerVariant.WARNING:
        return classes.warning
      case TimerVariant.DEFAULT:
        return classes.default
      default:
        return classes.default
    }
  }, [currentVariant, classes])

  const variantClass = useMemo(() => getVariantClass(), [getVariantClass])

  return (
    <Box className={classes.chipContainer}>
      <Tooltip title={t('dashboard.time_until_route_departure')} arrow>
        <Chip
          icon={<AccessTime fontSize='small' />}
          label={formatTime(remainingSeconds)}
          className={classnames(classes.chip, variantClass)}
          data-testid={`delivery-countdown-${currentVariant}`}
        />
      </Tooltip>
    </Box>
  )
}

export default DeliveryCountdown
